import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import CourseSelect from './selects/CourseSelect';
import KLineSelectorList from './KLineSelectorList';
import ModuleContext from 'CMFW/context/ModuleContext';
import { getCacheKey } from 'helpers/cache';
import { IRefIds } from 'constants/refs';
import { useCacheData } from 'hooks/useCacheData';
import CoursesSelectModel from '../models/CoursesSelectModel';

export interface ICourseKLineConfig {
  course_id: number;
  klines?: number[];
}

interface Props {
  value: ICourseKLineConfig[];
  onChange: (configs: ICourseKLineConfig[]) => void;
  className?: string;
}

const CourseKLineSelector: React.FC<Props> = ({ value, onChange, className }) => {
  const cacheKey = getCacheKey(IRefIds.courses, 0);
  const dataModel = useMemo(() => new CoursesSelectModel(), []);
  const cacheSelectData = useCacheData(cacheKey, dataModel.get.bind(dataModel));
  const handleCourseSelect = useCallback(
    (courseId: number) => {
      // Check if course already exists
      const exists = value.some((config) => config.course_id === courseId);
      if (exists) return;

      const newConfig: ICourseKLineConfig = {
        course_id: courseId,
        klines: [],
      };

      onChange([...value, newConfig]);
    },
    [value, onChange],
  );

  const handleRemoveCourse = useCallback(
    (courseId: number) => {
      onChange(value.filter((config) => config.course_id !== courseId));
    },
    [value, onChange],
  );

  const handleKLinesChange = useCallback(
    (courseId: number, klines: number[]) => {
      onChange(value.map((config) => (config.course_id === courseId ? { ...config, klines } : config)));
    },
    [value, onChange],
  );

  const handleAddAllCourses = useCallback(() => {
    if (!cacheSelectData) return;

    const a1b1CoursesConfigs: ICourseKLineConfig[] = cacheSelectData
      .filter((course) => course.name && (course.name.includes('A1') || course.name.includes('B1')))
      .map((course) => ({
        course_id: course.id,
        klines: [],
      }));

    onChange(a1b1CoursesConfigs);
  }, [cacheSelectData, onChange]);

  const getCourseName = useCallback(
    (courseId: number) => {
      if (cacheSelectData) {
        const course = cacheSelectData.find((c) => c.id === courseId);
        if (course) {
          return course.name;
        }
      }
      return `Course #${courseId}`;
    },
    [cacheSelectData],
  );

  return (
    <Wrapper className={className}>
      <AddCourseRow>
        <CourseSelect onChange={handleCourseSelect} skipCourseId={undefined} />
      </AddCourseRow>

      {value.length > 0 && (
        <CoursesList>
          {value.map((config) => (
            <CourseConfigItem key={config.course_id}>
              <CourseHeader>
                <CourseTitle>{getCourseName(config.course_id)}</CourseTitle>
                <RemoveButton onClick={() => handleRemoveCourse(config.course_id)}>Remove</RemoveButton>
              </CourseHeader>
              <KLineSection>
                <ModuleContext.Provider value={{ courseId: config.course_id } as any}>
                  <StyledKLineSelectorList
                    value={config.klines || []}
                    onChange={(klines) => handleKLinesChange(config.course_id, klines)}
                  />
                </ModuleContext.Provider>
              </KLineSection>
            </CourseConfigItem>
          ))}
        </CoursesList>
      )}

      {value.length === 0 && (
        <EmptyState>
          No courses selected.
          <AddAllCoursesLink onClick={handleAddAllCourses}>Add all courses (A1-B1)</AddAllCoursesLink>
        </EmptyState>
      )}
    </Wrapper>
  );
};

export default CourseKLineSelector;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddCourseRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const CoursesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 320px);
  gap: 16px;
  justify-content: start;
`;

const CourseConfigItem = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  background: #f9f9f9;

  &:hover {
    border-color: #bdbdbd;
  }
`;

const CourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CourseTitle = styled.h4`
  margin: 0;
  color: #333;
`;

const RemoveButton = styled(Button)`
  && {
    color: #d32f2f;
    border-color: #d32f2f;
  }
`;

const KLineSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const KLineLabel = styled.label`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const StyledKLineSelectorList = styled(KLineSelectorList)`
  // Any specific styling for the KLine selector in this context
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
  border: 2px dashed #ccc;
  border-radius: 6px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddAllCoursesLink = styled.button`
  background: transparent;
  border: none;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  font-style: normal;

  &:hover {
    color: #1565c0;
    text-decoration: none;
  }
`;
